'use client';
import React, { type ComponentPropsWithoutRef } from 'react';
import { Controller, type FieldPath, type FieldValues, useFormContext } from 'react-hook-form';

import {
  Select as BaseSelect,
  SelectContent,
  SelectItem,
  SelectTrigger as BaseSelectTrigger,
} from '@/slw/shared/ui/select';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/slw/shared/ui/base-form';
import { cn } from '@/slw/shared/lib/utils';

// ----------------------------------------------------------------------

type SelectOption = {
  label: string;
  value: string;
};

interface CustomRHFSelectProps<TFieldValues extends FieldValues>
  extends Omit<ComponentPropsWithoutRef<typeof Controller>, 'name' | 'control' | 'render'>,
    Omit<ComponentPropsWithoutRef<typeof BaseSelect>, 'name' | 'defaultValue'> {
  name: FieldPath<TFieldValues>;
  helperText?: string;
  containerClass?: string;
  label?: string;
  className?: string;
  options: SelectOption[];
  withError?: boolean;
  placeholder?: string;
  showMessError?: boolean;
  labelClass?: string;
  required?: boolean;
  direction?: 'row' | 'column';
}

export function Select<TFieldValues extends FieldValues>({
  name,
  label,
  helperText,
  required,
  defaultValue,
  showMessError = true,
  containerClass,
  className,
  options,
  placeholder = '',
  withError = true,
  direction = 'row',
  onValueChange,
  labelClass,
  ...props
}: CustomRHFSelectProps<TFieldValues>) {
  const { control } = useFormContext();
  const directionClass =
    direction === 'row' ? 'gap-2 items-center flex space-y-0' : 'gap-1 space-y-1 flex flex-col items-start';

  const handleValueChange = (value: string, onChange: (value: string) => void) => {
    if (value !== 'empty' && value) {
      onChange(value);
      onValueChange?.(value);

      return;
    }

    onValueChange?.('');
    onChange('');
  };

  return (
    <FormField
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field, fieldState: { error } }) => {
        // Get the selected label only when needed
        const selectedLabel = options.find((item) => item.value === field.value)?.label;

        return (
          <FormItem className={cn(directionClass, containerClass)}>
            {!!label && (
              <FormLabel className={cn('whitespace-nowrap font-normal text-sm leading-[1.5]', labelClass)}>
                {label}
                {required && <span className='text-[#dc2626]'>*</span>}
              </FormLabel>
            )}
            <BaseSelect
              onValueChange={(value) => handleValueChange(value, field.onChange)}
              value={field.value || 'empty'}
              {...props}
            >
              <FormControl>
                <BaseSelectTrigger
                  className={cn('w-full text-left mb-0 h-13.25', error && withError && 'border-destructive', className)}
                >
                  {selectedLabel || <span className='opacity-60'>{placeholder}</span>}
                </BaseSelectTrigger>
              </FormControl>
              <SelectContent className='z-[11000]' position='popper' sideOffset={4}>
                <div className='max-h-[300px] overflow-y-auto'>
                  {options.map((o, idx) => (
                    <SelectItem key={idx} value={o.value || 'empty'} className='font-medium'>
                      {o.label}
                    </SelectItem>
                  ))}
                </div>
              </SelectContent>
            </BaseSelect>
            {helperText && !error && withError && showMessError && (
              <p className='text-xs leading-[140%] text-muted-foreground'>{helperText}</p>
            )}
            {error && withError && showMessError && (
              <FormMessage className='py-0 text-xs leading-[140%]'>{error.message}</FormMessage>
            )}
          </FormItem>
        );
      }}
    />
  );
}
